import { Navigate, useLocation } from "react-router-dom";
import { useMenuAccess } from "../contexts/MenuAccessContext";
import { useRCN } from "../contexts/RCNContext";
import LoadingWrapper from "./common/LoadingWrapper";
import { CREATE_PO_PATH, WINE_SOL_LOCATION_ID, getRouteAccess } from "../utils/routeAccess";

const AccessMessage = ({ title, text }) => (
  <div className="flex flex-col items-center justify-center gap-2 py-24 text-center">
    <i className="pi pi-lock text-3xl text-gray-400" />
    <h2 className="text-lg font-semibold text-primaryBlack">{title}</h2>
    <p className="text-sm text-gray-500">{text}</p>
  </div>
);

// Renders the page only once the location's permissions are known and allow it, so a page
// for a disabled service never mounts (or calls its APIs) when its URL is typed in directly.
export default function RouteAccessGuard({ children }) {
  const { permissions, loading } = useMenuAccess();
  const { RCN, isApiLoaded } = useRCN();
  const { pathname, search } = useLocation();
  const queryParams = new URLSearchParams(search);
  const locationUid = queryParams.get("id");

  // The create-PO flow is opened by outside vendors and isn't tied to module permissions.
  if (pathname === CREATE_PO_PATH) return children;
  // Layout redirects any other create-po URL to CREATE_PO_PATH; don't render the page meanwhile.
  if (queryParams.get("create-po") === "true") return null;

  if (locationUid === WINE_SOL_LOCATION_ID) return children;

  // Permissions are only fetched once the RCN is known; without one they never arrive.
  const hasNoRCN = isApiLoaded && (!RCN || String(RCN).trim().length === 0);
  if (!permissions && (hasNoRCN || !loading)) {
    return (
      <AccessMessage
        title="Unable to verify access"
        text="We couldn't load the services enabled for this location. Please refresh or try again later."
      />
    );
  }
  if (loading || !permissions) return <LoadingWrapper />;

  const access = getRouteAccess({ pathname, permissions, locationUid });
  if (access.allowed) return children;

  if (access.fallbackPath) {
    const query = locationUid ? `?id=${encodeURIComponent(locationUid)}` : "";
    return <Navigate to={`${access.fallbackPath}${query}`} replace />;
  }

  return (
    <AccessMessage
      title="Service not enabled"
      text="This service is not enabled for this location."
    />
  );
}
